import { mdiClose, mdiRobotOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React from 'react';
import { isDemoModeActive } from '../../../services/auth/demo-mode.utils';
import { formatCodeChip, formatCodeComposite, IDenialCode } from '../../../services/denials/denial-code';
import { denialWorkerMatches, denialWorkerRuleName } from '../../../services/denials/denial-worker-rule';
import {
    AUTOMATION_WORKER_DEFINITIONS,
    RULE_FIELD_HELPERS,
} from '../../../services/worker-configuration/automation/worker-automation.constants';
import { readAutomationWorkerEnabled } from '../../../services/worker-configuration/automation/worker-automation.parser';
import { AutomationRule, RuleActionType } from '../../../services/worker-configuration/automation/worker-automation.types';
import {
    createEmptyRule,
    ENTER_TASK_FEE_DISCLAIMER,
    formatRuleMatchSummary,
    getAvailableRuleActionTypes,
    getDefaultActionValue,
    isRuleNameUniqueAcrossWorkers,
    RULE_ACTION_SELECT_LABELS,
    RULE_NAME_MAX_LENGTH,
} from '../../../services/worker-configuration/automation/worker-automation.utils';
import { testDenialRuleAgainstClaim } from '../../../services/worker-configuration/worker-rule-tester.api';
import { WorkerStore } from '../../../stores/worker-configuration/worker.store';
import { BaseButton, BaseInput, BaseSelect, ButtonGroup, IButtonGroupItemProps, ISelectOption } from '../../base';
import { ConfigurationActionSummary, ConfigurationFieldLabel } from '../rules/configuration-action-summary';
import { ConfigurationRulePayerEditor } from '../rules/configuration-rule-payer-editor';
import * as R from '../rules/configuration-rule.styles';
import * as S from './denial-worker-dialog.styles';

/**
 * "Configure worker" for one denials-table code group (FE-75).
 *
 * The design's modal, bound to what the worker engine actually has — verified in enter-worker and the
 * platform spec on 2026-09-13, decided with Vadim the same day:
 *
 * - **TRUST** is shown, but "Queue for my approval" is disabled: there is no approval queue (FE-73 · B9).
 *   An active rule acts the moment a claim matches, and the dialog says so. The design's "$5,000 always
 *   routes for approval" line does not appear: no such policy exists anywhere (NS-3).
 * - **Balance** is shown disabled: no rule scope takes an amount (B10).
 * - **THEN** offers the engine's four actions; the design's other two are listed disabled (B11).
 * - **Test on a claim** asks the platform's own matcher about one claim. There is no backtest: nothing can
 *   evaluate a rule against history, so no count or dollar figure appears here.
 *
 * Saving replaces the account's WHOLE workflow configuration (create-or-replace POST), so Activate starts
 * from a fresh fetch and refuses while the Workers page holds unsaved edits in the same store.
 */

const MISSING_ACTION_REASON = 'The worker engine has no such action yet (FE-73 · B11).';
const APPROVAL_QUEUE_REASON =
    'Needs an approval queue in the backend (FE-73 · B9). Today an active rule acts as soon as a claim matches.';
const BALANCE_REASON = 'Rules cannot match on claim balance yet (FE-73 · B10).';
// Unlike MISSING_ACTION_REASON, the engine supports this action fine — only this quick-add dialog
// has no editor for its function/coder-rules value. Listing it as selectable here left "Then"
// showing nothing to configure and Activate failing with a generic "add the JavaScript" error
// (PR review, 2026-09-28): disabled, with its own reason, same as the two engine-missing actions.
const EXECUTE_FUNCTION_NOT_HERE_REASON =
    "This dialog doesn't have the code editor for it — add or edit this action from the rule's full editor on the Workers page instead.";
const UNSUPPORTED_PREFIX = 'UNSUPPORTED_';

const MISSING_ACTIONS: ISelectOption<string>[] = [
    { label: 'Apply Enter Coder rule and retransmit', value: `${UNSUPPORTED_PREFIX}CODER_RULE`, title: MISSING_ACTION_REASON },
    { label: 'Resubmit as corrected claim', value: `${UNSUPPORTED_PREFIX}CORRECTED_CLAIM`, title: MISSING_ACTION_REASON },
];

const TRUST_OPTIONS: IButtonGroupItemProps[] = [
    { label: 'Run unattended' },
    { label: 'Queue for my approval', disabled: true, title: APPROVAL_QUEUE_REASON },
];

const OWNER_OPTIONS: IButtonGroupItemProps[] = [{ label: 'ENTER' }, { label: 'PROVIDER' }];

const ANY_AMOUNT: ISelectOption<string> = { label: 'Any amount', value: 'ANY' };

const isUnsupported = (value: unknown): boolean => String(value).startsWith(UNSUPPORTED_PREFIX);

export interface IDenialWorkerDialogProps {
    /** The code group the rule is for. Pre-filled from the row; not editable here. */
    code: IDenialCode;
    /** The group's official reason text, shown beside the code. */
    reasonDescription?: string;
    store: WorkerStore;
    onClose: () => void;
    /** Called once the configuration has saved. */
    onActivated?: (rule: AutomationRule) => void;
    /** The Workers page, for what this dialog does not cover: documents, assignees, ordering. */
    workersHref?: string;
}

export const DenialWorkerDialog: React.FC<IDenialWorkerDialogProps> = observer(
    ({ code, reasonDescription, store, onClose, onActivated, workersHref }) => {
        const [payerIds, setPayerIds] = React.useState<string[]>([]);
        const [actionType, setActionType] = React.useState<RuleActionType>('CREATE_APPEAL');
        const [actionValue, setActionValue] = React.useState<Record<string, unknown>>(() =>
            getDefaultActionValue('CREATE_APPEAL'),
        );
        const [claimId, setClaimId] = React.useState('');
        const [testing, setTesting] = React.useState(false);
        const [testResult, setTestResult] = React.useState<{ message: string; failed: boolean } | null>(null);
        const [activating, setActivating] = React.useState(false);
        const [activateError, setActivateError] = React.useState<string | null>(null);
        const demo = isDemoModeActive();

        React.useEffect(() => {
            if (!store.configuration && !store.loading) {
                void store.fetchConfiguration();
            }
        }, [store]);

        const worker = AUTOMATION_WORKER_DEFINITIONS.denial;
        const denialWorker = store.draftAutomationWorkers.denial;
        const workerOn = readAutomationWorkerEnabled(store.draftConfig, denialWorker);

        const actionOptions: ISelectOption<string>[] = [
            ...getAvailableRuleActionTypes('denial')
                // EXECUTE_ENTER_FUNCTION is real, but this dialog has no function editor/coder-rules
                // picker to configure it with — shown disabled below instead of silently mis-offered.
                .filter(type => type !== 'EXECUTE_ENTER_FUNCTION')
                .map(type => ({ label: RULE_ACTION_SELECT_LABELS[type], value: type })),
            {
                label: RULE_ACTION_SELECT_LABELS.EXECUTE_ENTER_FUNCTION,
                value: `${UNSUPPORTED_PREFIX}EXECUTE_ENTER_FUNCTION`,
                title: EXECUTE_FUNCTION_NOT_HERE_REASON,
            },
            ...MISSING_ACTIONS,
        ];
        const selectedAction = actionOptions.find(option => option.value === actionType);
        const selectedOwner = actionValue.messageOwner === 'PROVIDER' ? OWNER_OPTIONS[1] : OWNER_OPTIONS[0];

        const payerScope =
            payerIds.length === 0 ? 'All payers' : payerIds.length === 1 ? '1 payer' : `${payerIds.length} payers`;
        const scope = `${formatCodeComposite(code)} · ${payerScope} · ${worker.name} (${worker.scopeLabel})`;

        const chooseAction = (type: RuleActionType): void => {
            setActionType(type);
            setActionValue(getDefaultActionValue(type));
            setTestResult(null);
        };

        const setField = (field: string, value: unknown): void =>
            setActionValue(current => ({ ...current, [field]: value }));

        const buildRule = (): AutomationRule => {
            const name = denialWorkerRuleName(code, payerIds, {
                isNameTaken: candidate => !isRuleNameUniqueAcrossWorkers(store.draftAutomationWorkers, candidate),
                maxLength: RULE_NAME_MAX_LENGTH,
            });
            return {
                ...createEmptyRule('PAYMENT', name),
                match: denialWorkerMatches(code, payerIds),
                action: { type: actionType, value: actionValue },
            };
        };

        const closeoutMissing = actionType === 'CLOSE_OUT' && !String(actionValue.closeoutDescription ?? '').trim();
        let activateBlocked: string | null = null;
        if (demo) {
            activateBlocked = 'Saving is switched off in demo mode.';
        } else if (store.readOnly) {
            activateBlocked = 'This console can view worker configuration but not change it.';
        } else if (store.dirty && !store.hasRecommendedDefaultUpdatesOnly) {
            // Only someone's own edits on the Workers page block this. An account whose saved configuration predates an
            // option gets that option's default filled into the draft on every load, which also reads as dirty; that
            // blocked every activation on such accounts, although nobody had changed anything.
            activateBlocked = 'The Workers page has unsaved changes. Save or discard them there first.';
        } else if (closeoutMissing) {
            activateBlocked = 'Add a closeout reason first.';
        }

        const runTest = async (): Promise<void> => {
            const id = claimId.trim();
            if (!id || demo || testing) {
                return;
            }
            setTesting(true);
            setTestResult(null);
            try {
                const result = await testDenialRuleAgainstClaim(id, buildRule(), store.apiMode);
                setTestResult({
                    message: result.matched
                        ? `Matches claim ${id} as it is now.`
                        : `Does not match claim ${id} as it is now.`,
                    failed: false,
                });
            } catch (err) {
                setTestResult({ message: err instanceof Error ? err.message : 'The test could not run.', failed: true });
            } finally {
                setTesting(false);
            }
        };

        const activate = async (): Promise<void> => {
            if (activateBlocked || activating) {
                return;
            }
            setActivating(true);
            setActivateError(null);

            // The save replaces the account's whole configuration, so start from what the server has now.
            await store.fetchConfiguration();
            if (store.error) {
                setActivateError(store.error);
                setActivating(false);
                return;
            }

            // The fresh fetch fills option defaults the saved configuration lacks into the draft. Drop them, so
            // activating adds this one rule and nothing else: those defaults stay for the Workers page to offer.
            if (store.hasRecommendedDefaultUpdatesOnly) {
                store.discardChanges();
            }
            const rule = buildRule();
            store.appendAutomationRule('denial', rule);
            const saved = await store.saveConfiguration();
            if (!saved) {
                const message =
                    store.saveError || store.automationRuleValidationError || 'The configuration was not saved.';
                // Drop the unsaved append, so the Workers page does not inherit a draft nobody made there.
                store.discardChanges();
                setActivateError(message);
                setActivating(false);
                return;
            }

            setActivating(false);
            onActivated?.(rule);
        };

        return (
            <S.Dialog>
                <S.Header>
                    <Icon path={mdiRobotOutline} size={0.9} aria-hidden={true} />
                    <S.HeaderText>
                        <S.Title>Enter Worker Configuration</S.Title>
                        <S.Scope>Scope: {scope}</S.Scope>
                    </S.HeaderText>
                    <BaseButton variant="text" size="small" type="button" aria-label="Close" onClick={onClose}>
                        <Icon path={mdiClose} size={0.8} />
                    </BaseButton>
                </S.Header>

                <S.Section>
                    <S.Eyebrow>New configuration</S.Eyebrow>
                    <S.Draft>
                        <S.Row>
                            <S.RowTag>When</S.RowTag>
                            <S.CodeChip>{formatCodeChip(code)}</S.CodeChip>
                            {reasonDescription && <S.ReasonText title={reasonDescription}>{reasonDescription}</S.ReasonText>}
                        </S.Row>

                        <S.Row>
                            <S.RowTag />
                            <S.Joiner>and payer</S.Joiner>
                            <S.Field>
                                <ConfigurationRulePayerEditor
                                    store={store}
                                    payerIds={payerIds}
                                    onChange={ids => {
                                        setPayerIds(ids);
                                        setTestResult(null);
                                    }}
                                    pickerKey="denial-worker-dialog|payer"
                                    disabled={activating}
                                />
                            </S.Field>
                            <S.Joiner>and balance</S.Joiner>
                            <S.Slot title={BALANCE_REASON}>
                                <S.NarrowField>
                                    <BaseSelect options={[ANY_AMOUNT]} value={ANY_AMOUNT} required disabled />
                                </S.NarrowField>
                            </S.Slot>
                        </S.Row>
                        <S.Details>
                            <S.Note>No payer chosen means any payer.</S.Note>
                        </S.Details>

                        <S.Row>
                            <S.RowTag>Then</S.RowTag>
                            <S.Field>
                                <BaseSelect
                                    options={actionOptions}
                                    value={selectedAction}
                                    required
                                    disabled={activating}
                                    isOptionDisabled={(option: ISelectOption): boolean => isUnsupported(option.value)}
                                    onChange={(selected: unknown): void => {
                                        const option = selected as ISelectOption<string> | null;
                                        if (option?.value && !isUnsupported(option.value)) {
                                            chooseAction(option.value as RuleActionType);
                                        }
                                    }}
                                />
                            </S.Field>
                        </S.Row>
                        <S.Details>
                            <ConfigurationActionSummary actionType={actionType} />

                            {actionType === 'CLOSE_OUT' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.closeoutDescription}>
                                        Closeout reason
                                    </ConfigurationFieldLabel>
                                    <R.FieldInput
                                        type="text"
                                        maxLength={45}
                                        disabled={activating}
                                        value={String(actionValue.closeoutDescription ?? '')}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>): void =>
                                            setField('closeoutDescription', event.target.value.slice(0, 45))
                                        }
                                    />
                                </div>
                            )}

                            {actionType === 'SWITCH_TO_PRIVATE_PAY' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.memo} optional>
                                        Invoice memo
                                    </ConfigurationFieldLabel>
                                    <R.FieldTextarea
                                        rows={3}
                                        disabled={activating}
                                        value={String(actionValue.memo ?? '')}
                                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                                            setField('memo', event.target.value)
                                        }
                                    />
                                </div>
                            )}

                            {actionType === 'CREATE_APPEAL' && (
                                <div>
                                    <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.prompt} optional>
                                        Customize the cover letter
                                    </ConfigurationFieldLabel>
                                    <R.FieldTextarea
                                        rows={3}
                                        disabled={activating}
                                        value={String(actionValue.prompt ?? '')}
                                        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                                            setField('prompt', event.target.value)
                                        }
                                    />
                                    <S.Note>Attach documents to the packet on the Workers page after activating.</S.Note>
                                </div>
                            )}

                            {actionType === 'CREATE_MESSAGE' && (
                                <>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.messageOwner}>
                                            Responsibility owner
                                        </ConfigurationFieldLabel>
                                        <ButtonGroup
                                            variant="pills"
                                            buttons={OWNER_OPTIONS}
                                            selected={selectedOwner}
                                            onSelect={(item): void => setField('messageOwner', item.label)}
                                        />
                                    </div>
                                    <div>
                                        <ConfigurationFieldLabel helper={RULE_FIELD_HELPERS.suggestedResolution}>
                                            Suggested next steps
                                        </ConfigurationFieldLabel>
                                        <R.FieldTextarea
                                            rows={2}
                                            disabled={activating}
                                            value={String(actionValue.suggestedResolution ?? '')}
                                            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>): void =>
                                                setField('suggestedResolution', event.target.value)
                                            }
                                        />
                                        {selectedOwner.label === 'ENTER' && <S.Note>{ENTER_TASK_FEE_DISCLAIMER}</S.Note>}
                                        <S.Note>Category and assignees can be changed on the Workers page.</S.Note>
                                    </div>
                                </>
                            )}
                        </S.Details>

                        <S.Row>
                            <S.RowTag>Trust</S.RowTag>
                            <ButtonGroup variant="pills" buttons={TRUST_OPTIONS} selected={TRUST_OPTIONS[0]} />
                        </S.Row>
                        <S.Details>
                            <S.Consequence>
                                Once activated, this rule acts on every denied claim it matches, as soon as it matches.
                                There is no approval step yet.
                            </S.Consequence>
                        </S.Details>

                        <S.Footer>
                            <S.TestInput>
                                <BaseInput
                                    value={claimId}
                                    placeholder="Claim ID to test against"
                                    aria-label="Claim ID to test against"
                                    disabled={demo || testing}
                                    disableLatin1Validation={true}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
                                        setClaimId(event.target.value);
                                        setTestResult(null);
                                    }}
                                />
                            </S.TestInput>
                            <BaseButton
                                variant="outlined"
                                size="small"
                                disabled={demo || testing || !claimId.trim()}
                                onClick={(): void => void runTest()}>
                                {testing ? 'Testing…' : 'Test on a claim'}
                            </BaseButton>
                            {testResult && (
                                <S.TestResult failed={testResult.failed} role="status">
                                    {testResult.message}
                                </S.TestResult>
                            )}
                            <S.Spacer />
                            <S.Slot title={activateBlocked ?? undefined}>
                                <BaseButton
                                    variant="primary"
                                    size="small"
                                    disabled={Boolean(activateBlocked) || activating || store.loading}
                                    onClick={(): void => void activate()}>
                                    {activating ? 'Activating…' : 'Activate configuration'}
                                </BaseButton>
                            </S.Slot>
                        </S.Footer>
                        {demo && <S.Note>Demo mode: testing and saving reach live systems, so both are switched off.</S.Note>}
                        {!demo && activateBlocked && <S.Note>{activateBlocked}</S.Note>}
                        {activateError && <S.ErrorText role="alert">{activateError}</S.ErrorText>}
                    </S.Draft>
                </S.Section>

                <S.Section>
                    <S.Eyebrow>Active configurations</S.Eyebrow>
                    {store.loading && <S.Note>Loading the denial worker&apos;s rules…</S.Note>}
                    {!store.loading && store.error && <S.ErrorText>{store.error}</S.ErrorText>}
                    {!store.loading && !store.error && denialWorker.rules.length === 0 && (
                        <S.Note>No denial rules yet.</S.Note>
                    )}
                    {!store.loading && !store.error && denialWorker.rules.length > 0 && (
                        <S.ActiveList>
                            {denialWorker.rules.map(rule => (
                                <S.ActiveCard key={rule.id}>
                                    <S.ActiveText>
                                        <S.ActiveTitle>{rule.name}</S.ActiveTitle>
                                        <S.ActiveMeta>
                                            {formatRuleMatchSummary(
                                                rule.match,
                                                store.catalog,
                                                store.payerById,
                                                store.providerById,
                                                store.facilityById,
                                            )}{' '}
                                            → {RULE_ACTION_SELECT_LABELS[rule.action.type] ?? rule.action.type}
                                        </S.ActiveMeta>
                                    </S.ActiveText>
                                    <S.StateBadge workerOn={workerOn}>{workerOn ? 'Active' : 'Worker off'}</S.StateBadge>
                                </S.ActiveCard>
                            ))}
                        </S.ActiveList>
                    )}
                    {workersHref && (
                        <S.WorkersLink to={workersHref} onClick={onClose}>
                            Open the Workers page to edit, reorder or switch off rules
                        </S.WorkersLink>
                    )}
                </S.Section>
            </S.Dialog>
        );
    },
);
